import { useState, useEffect, useRef, useCallback } from 'react';
import { useChat } from '../context/ChatContext';

// Typing jelzés kezelésére szolgáló hook
export function useTyping(chatId) {
  const { sendTypingStatus, typingUsers, currentUser } = useChat();
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const lastTypingTimeRef = useRef(0);

  // Typing időzítő törlése unmount-nál
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Typing jelzés indítása
  const startTyping = useCallback(() => {
    if (!chatId || !currentUser) return;

    const now = Date.now();
    lastTypingTimeRef.current = now;

    if (!isTyping) {
      setIsTyping(true);
      sendTypingStatus(chatId, true);
    }

    // Typing leállítása 3 másodperc múlva
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      // Csak akkor állítsuk le, ha ez volt az utolsó typing esemény
      if (now === lastTypingTimeRef.current) {
        stopTyping();
      }
    }, 3000);
  }, [chatId, currentUser, isTyping, sendTypingStatus]);

  // Typing jelzés leállítása
  const stopTyping = useCallback(() => {
    if (!chatId || !currentUser || !isTyping) return;

    setIsTyping(false);
    sendTypingStatus(chatId, false);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, [chatId, currentUser, isTyping, sendTypingStatus]);

  // Aktuális chat typing felhasználói (saját user kizárásával)
  const currentChatTypingUsers = chatId && typingUsers[chatId] 
    ? Object.values(typingUsers[chatId]).filter(user => user.userId !== currentUser?.id)
    : [];

  // Typing felhasználók neveinek listája
  const typingUserNames = currentChatTypingUsers.map(user => user.userName);

  // Typing szöveg generálása
  const getTypingText = useCallback(() => {
    const count = typingUserNames.length;
    
    if (count === 0) return '';
    if (count === 1) return `${typingUserNames[0]} gépel...`;
    if (count === 2) return `${typingUserNames[0]} és ${typingUserNames[1]} gépelnek...`;
    return `${typingUserNames[0]} és ${count - 1} másik személy gépel...`;
  }, [typingUserNames]);

  return {
    isTyping,
    startTyping,
    stopTyping,
    typingUserNames,
    typingText: getTypingText(),
    hasTypingUsers: typingUserNames.length > 0
  };
}

export default useTyping;
